/**
 * Integrações com aparelhos (spec 022 — exames do OCT Triton chegando
 * sozinhos na ficha). O backend é a function `integracoes`
 * (functions/src/integrations/app.ts); aqui ficam o que o app lê do
 * Firestore (tokens e a fila "Exames sem paciente") e as ações da equipe,
 * que passam pela function — as regras não deixam o app gravar nessas
 * coleções.
 */
import { collection, onSnapshot, query, where, type Unsubscribe } from 'firebase/firestore';
import { auth, db, USING_EMULATORS } from '@/services/firebase/config';

export interface IntegrationToken {
  id: string;
  name: string;
  /** Começo do token ("dta_ab12cd…") — pra reconhecer qual está em qual aparelho. */
  tokenStart: string;
  createdAt: number;
  createdBy: string;
  revokedAt: number | null;
  lastUsedAt: number | null;
}

export type InboxMotivo = 'nao_encontrado' | 'mais_de_uma_ficha' | 'nome_nao_confere' | 'codigo_removido';

export interface IntegrationInboxItem {
  id: string;
  status: 'pendente' | 'processando';
  motivo: InboxMotivo;
  /** paciente_id que o aparelho mandou: o CPF no OCT, o código do AnaEyes (P0123456789) no topógrafo. */
  octPatientId: string;
  /** Chave código + equipamento (`null` sem equipamento; ausente nos itens de antes de 22/10/2026). */
  deviceKey?: string | null;
  octPatientName: string | null;
  octBirthDate: string | null;
  examDate: number;
  examType: string;
  eye: 'OD' | 'OE' | 'AO' | null;
  equipment: string | null;
  description: string | null;
  deviceName: string;
  receivedAt: number;
  file: { name: string; url: string; type: string; storagePath: string; size: number };
}

/** O paciente_id é um CPF (11 dígitos, com ou sem pontos e traço) — o caso do OCT. */
export function isCpfCode(code: string): boolean {
  return /^[\d.\-\s]+$/.test(code) && code.replace(/\D/g, '').length === 11;
}

export function inboxMotivoLabel(motivo: InboxMotivo, code: string): string {
  const isCpf = isCpfCode(code);
  switch (motivo) {
    case 'nao_encontrado':
      return isCpf ? 'Nenhuma ficha com esse CPF' : 'Código ainda sem ficha';
    case 'mais_de_uma_ficha':
      return 'CPF em mais de uma ficha';
    case 'nome_nao_confere':
      return 'Nome do aparelho não confere com a ficha';
    case 'codigo_removido':
      return 'Voltou da ficha (código removido)';
    default:
      return motivo;
  }
}

/** Código de aparelho memorizado na ficha (`clinics/{cid}/deviceCodes`, só o backend grava). */
export interface PatientDeviceCode {
  id: string;
  code: string;
  equipment: string;
  patientId: string;
  createdBy: string;
  createdByName: string | null;
  createdAt: number;
  examCount: number;
}

export function subscribeIntegrationTokens(
  clinicId: string,
  cb: (tokens: IntegrationToken[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  return onSnapshot(
    collection(db, 'clinics', clinicId, 'integrationTokens'),
    (snap) => cb(snap.docs.map((d) => ({ ...(d.data() as IntegrationToken), id: d.id })).sort((a, b) => b.createdAt - a.createdAt)),
    (err) => onError?.(err),
  );
}

/** Fila "Exames sem paciente", do exame mais novo pro mais antigo. */
export function subscribeIntegrationInbox(
  clinicId: string,
  cb: (items: IntegrationInboxItem[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  return onSnapshot(
    collection(db, 'clinics', clinicId, 'integrationInbox'),
    (snap) =>
      cb(snap.docs.map((d) => ({ ...(d.data() as IntegrationInboxItem), id: d.id })).sort((a, b) => b.examDate - a.examDate)),
    (err) => onError?.(err),
  );
}

export function subscribePatientDeviceCodes(
  clinicId: string,
  patientId: string,
  cb: (codes: PatientDeviceCode[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  return onSnapshot(
    query(collection(db, 'clinics', clinicId, 'deviceCodes'), where('patientId', '==', patientId)),
    (snap) => cb(snap.docs.map((d) => ({ ...(d.data() as PatientDeviceCode), id: d.id })).sort((a, b) => b.createdAt - a.createdAt)),
    (err) => onError?.(err),
  );
}

// ─── Ações (function `integracoes`) ──────────────────────────────────────

/** URL pública que a ponte usa (rewrite do Hosting). Mostrada na tela de Integrações. */
export const PUBLIC_API_BASE = 'https://dratento.com.br/api/integracoes';

function endpoint(path: string): string {
  // Build do emulador: a function roda local (scripts/emulator/integracoes-local.mjs), nunca a de produção.
  if (USING_EMULATORS) return `http://127.0.0.1:5189${path}`;
  const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID || 'dratento';
  return `https://us-central1-${projectId}.cloudfunctions.net/integracoes${path}`;
}

async function call<T>(path: string, body?: unknown): Promise<T> {
  const user = auth.currentUser;
  if (!user) throw new Error('Faça login de novo.');
  const res = await fetch(endpoint(path), {
    method: 'POST',
    headers: { Authorization: `Bearer ${await user.getIdToken()}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body ?? {}),
  });
  const data = (await res.json().catch(() => ({}))) as { mensagem?: string };
  if (!res.ok) throw new Error(data.mensagem || `Falha (HTTP ${res.status}).`);
  return data as T;
}

export const IntegrationsApi = {
  createToken: (nome: string) =>
    call<{ token_id: string; nome: string; token: string }>('/tokens', { nome }),
  revokeToken: (tokenId: string) => call<{ ok: true }>(`/tokens/${encodeURIComponent(tokenId)}/revogar`),
  linkPending: (itemId: string, patientId: string) =>
    call<{ ok: true; anexo_id: string; paciente_id: string }>(`/pendentes/${encodeURIComponent(itemId)}/vincular`, { patientId }),
  /** Uma linha da fila. `lembrar` grava o código e leva junto todos os pendentes com o mesmo código + equipamento. */
  linkPendingGroup: (pendenteIds: string[], patientId: string, lembrar: boolean) =>
    call<{ ok: true; vinculados: number; falhas: number; codigo_id: string | null }>('/pendentes/vincular', {
      pendenteIds,
      patientId,
      lembrar,
    }),
  discardPending: (itemId: string) => call<{ ok: true }>(`/pendentes/${encodeURIComponent(itemId)}/descartar`),
  /** `devolver` tira da ficha os exames que entraram por esse código e devolve pra fila. */
  removeDeviceCode: (codeId: string, devolver: boolean) =>
    call<{ ok: true; devolvidos: number }>(`/codigos/${encodeURIComponent(codeId)}/remover`, { devolver }),
};
